'use client'

import React from 'react'
import classNames from 'classnames'
import { Media } from '@/payload-types'
import { Icon } from '@/ui/atoms/Icon'
import { Label } from '@/ui/atoms/Label'
import { Button } from '@/ui/atoms/Button'
import { getFileType } from '@/utils/getFileType'
import { getResourceIcon } from '@/utils/resourceIconMap'
import { useResourceDownload } from './useResourceDownload'
import { useResourceDownloadButtonText } from './getButtonText'

export type ResourceDownloadCardProps = {
  title: string
  description?: string
  file: Media
  date?: Date | string | null
  pageCount?: number
  price?: number
  metainfo?: string | null
  isLocked: boolean
  downloadUrl: string
  signUpRedirectUrl?: string
  resourceType?: string | null
  className?: string
  isGated?: boolean
  lockedButtonTitle?: string
}

export const ResourceDownloadCard: React.FC<ResourceDownloadCardProps> = (props) => {
  const {
    title,
    description,
    file,
    date,
    pageCount,
    price = 0,
    metainfo,
    isLocked,
    downloadUrl,
    signUpRedirectUrl,
    resourceType,
    className: classNameFromProps,
    isGated = false,
    lockedButtonTitle,
  } = props

  const fileType = getFileType(file)
  const formattedDate = date ? new Date(date).toLocaleDateString('de-DE') : null
  const iconName = getResourceIcon(resourceType)

  const { handleDownload, isLoading } = useResourceDownload({
    file,
    downloadUrl,
    isGated,
    isLocked,
    signUpRedirectUrl,
  })

  const buttonText = useResourceDownloadButtonText({
    isGated,
    isLocked,
    lockedButtonTitle,
  })

  return (
    <div
      className={classNames(
        'flex flex-col rounded-md overflow-hidden bg-bg-subtle border border-border-subtle',
        classNameFromProps,
      )}
    >
      <div className="flex flex-col p-4">
        {/* Top section: Icon, File Type/Page Count/Meta Info, and Price Badge */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className="size-9 rounded-sm flex items-center justify-center relative overflow-hidden bg-bg-brand-blue flex-shrink-0">
              <Icon name={iconName} parentClass={'z-20'} className={'text-text-accent !size-5'} />
            </div>

            {/* File Type, Page Count, and Meta Info */}
            {(fileType || pageCount || metainfo) && (
              <div className="flex items-center gap-2 text-label-XS-regular text-text-faded">
                {fileType && <span className={'font-semibold'}>{fileType}</span>}
                {fileType && (pageCount || metainfo) && (
                  <div className="w-1 h-1 rounded-full bg-text-faded" />
                )}
                {pageCount && <span>{pageCount} pages</span>}
                {pageCount && metainfo && <div className="w-1 h-1 rounded-full bg-text-faded" />}
                {metainfo && <span>{metainfo}</span>}
              </div>
            )}
          </div>

          {/* Price Badge */}
          {price === 0 ? (
            <Label
              title="Free"
              variant="success"
              size="small"
              className="bg-green-90 text-green-20 flex-shrink-0"
            />
          ) : (
            <Label
              title={`${price} $`}
              variant="neutral"
              size="small"
              className="bg-neutral-90 text-neutral-20 flex-shrink-0"
            />
          )}
        </div>

        {/* Title */}
        <h3 className="text-headline-S-semibold text-text-base mb-4 transition-colors">{title}</h3>

        {/* Description */}
        {description && (
          <p className="text-body-S-regular text-text-faded mb-4 line-clamp-3">{description}</p>
        )}

        {/* Date */}
        {formattedDate && (
          <div className="flex items-center gap-2 text-label-XS-regular text-text-faded mb-6">
            <span>{formattedDate}</span>
          </div>
        )}

        {/* Download Button */}
        <Button
          variant="primary"
          size="lg"
          className={classNames('w-full justify-center', isLoading && 'animate-pulse')}
          onClick={handleDownload}
          disabled={isLoading}
        >
          <Icon name="download-2-line" className="size-4 text-text-inverted" />
          <span>{buttonText}</span>
        </Button>
      </div>
    </div>
  )
}
